import { memo } from 'react'

interface SearchableTextProps {
  /**
   * The text to render with bolded search matches
   */
  text: string

  /**
   * The search query to match against
   */
  searchQuery: string
}

/**
 * Component that renders text with matching search terms bolded.
 *
 * Memoized to prevent unnecessary regex operations when rendered in every table row.
 *
 * Note: Uses a custom implementation rather than a library like react-highlight-words.
 * This keeps the bundle small (~60 lines) and gives us full control over the behavior.
 * Consider switching to a library if we need fuzzy matching, word boundaries, or complex
 * highlighting rules.
 *
 * @example
 * <SearchableText text="Cat vs Dog" searchQuery="dog cat" />
 * // Renders: <><strong>Cat</strong> vs <strong>Dog</strong></>
 */
export const SearchableText = memo(function SearchableText({ text, searchQuery }: SearchableTextProps) {
  if (!searchQuery || !text) {
    return <>{text}</>
  }

  const searchTerms = searchQuery
    .toLowerCase()
    .split(/\s+/)
    .filter((term) => term.length > 0)

  if (searchTerms.length === 0) {
    return <>{text}</>
  }

  // Create a regex that matches any of the search terms (case-insensitive)
  // Escape special regex characters (. * + ? ^ $ { } ( ) | [ ] \) so they're treated as literals
  const escapedTerms = searchTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi')

  // Split the text by matches
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, index) => {
        // Skip empty strings that can occur after splitting
        if (part === '') return null

        // Check if this part matches any search term (case-insensitive)
        const isMatch = searchTerms.some((term) => part.toLowerCase() === term)

        // Matching text
        if (isMatch) {
          return (
            <strong key={`${index}-${part}`} className="font-semibold">
              {part}
            </strong>
          )
        }

        // Non-matching text
        return <span key={`${index}-${part}`}>{part}</span>
      })}
    </>
  )
})
